import { Breadcrumb, Icon, IconButton, MessageBar, MessageBarType, Spinner, SpinnerSize, TextField, TooltipHost, ITooltipProps } from '@fluentui/react';
import * as React from 'react';

import { SubscriberRecord } from '../../../Common/ConsumerModels/SubscriberRecord';
import * as Styled from '../../../Common/Styles/portalManagement.styled';
import { TeamsLink } from './TeamsLink';
import { ExpirationUtils } from '../../../Common/CommonModels/Constants';

type SubscriberDetailsHeaderProps = {
  subscriberRecord: SubscriberRecord;
  isAdmin: boolean;
};

export function SubscriberDetailsHeaderLoading(): React.ReactElement {
  return (
    <Styled.LoadingContainer data-testid="heading-section-loading">
      <Spinner size={SpinnerSize.large} />
    </Styled.LoadingContainer>
  );
}

export function SubscriberDetailsHeader(props: SubscriberDetailsHeaderProps): React.ReactElement {
  const { subscriberRecord, isAdmin } = props;
  const getTooltipProps=(accountName: string) : ITooltipProps => ({
    onRenderContent: () => {
      return (
       <span>abfss://publish@{accountName}.dfs.core.windows.net/subscriptions/{subscriberRecord.id}/tenants/&lt;data owner id&gt;/types/&lt;CDM Type&gt;/versions/&lt;version&gt;</span>
     );
    },
  });

  const prodSubscriptionStatusIcon = subscriberRecord.prodSubscriptionExpirationStatus !== null 
    ? ExpirationUtils.getStatusIcon(subscriberRecord.prodSubscriptionExpirationStatus)
    : null;

  const nonProdSubscriptionStatusIcon = subscriberRecord.nonProdSubscriptionExpirationStatus !== null
    ? ExpirationUtils.getStatusIcon(subscriberRecord.nonProdSubscriptionExpirationStatus)
    : null;

  return (
    <Styled.HeaderContainer data-testid="heading-container">
      <Breadcrumb
        styles={Styled.portalManagementStyles}
        ariaLabel="Breadcrumb bar with consumers page link and current consumer name"
        items={[
          {
            href: '/consumers',
            key: 'consumers',
            text: 'Consumers',
          },
          {
            isCurrentItem: true,
            key: 'consumerName',
            text: subscriberRecord.name,
          },
        ]}
      />
      <Styled.DetailsContainer>
        <title>{subscriberRecord.name}</title>

        <Styled.PortalManagementTitleText>{subscriberRecord.name}</Styled.PortalManagementTitleText>

        <Styled.PortalManagementDetails>
          <Styled.TypeValueContainer>
            <Styled.Type>Consumer ID</Styled.Type>
            <Styled.Value>{subscriberRecord.id}</Styled.Value>
          </Styled.TypeValueContainer>
          <Styled.TypeValueContainer>
            <Styled.Type>Security Group</Styled.Type>
            <Styled.Value>{subscriberRecord.securityGroup}</Styled.Value>
          </Styled.TypeValueContainer>
          <Styled.TypeValueContainer>
            <Styled.Type>Link to Support Channel</Styled.Type>
            <TeamsLink subscriberRecord={subscriberRecord} isAdmin={isAdmin}/>
          </Styled.TypeValueContainer>
          {isAdmin && subscriberRecord.prodSubscriptionExpirationDate && subscriberRecord.prodSubscriptionExpirationStatus !== null && (
          <Styled.TypeValueContainer>
            <Styled.Type>Subscription Expiration Date (PROD)</Styled.Type>
            <Styled.Value>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {ExpirationUtils.formatDate(subscriberRecord.prodSubscriptionExpirationDate)}
              {prodSubscriptionStatusIcon && (
                <Icon
                  iconName={prodSubscriptionStatusIcon.iconName}
                  aria-label={`Production subscription status: ${ExpirationUtils.getStatusText(subscriberRecord.prodSubscriptionExpirationStatus)}`}
                  styles={{ root: { fontSize: 18, color: prodSubscriptionStatusIcon.color } }}
                />
              )}
              </span>
            </Styled.Value>
          </Styled.TypeValueContainer>
          )}
          {isAdmin && subscriberRecord.nonProdSubscriptionExpirationDate && subscriberRecord.nonProdSubscriptionExpirationStatus !== null && (
          <Styled.TypeValueContainer>
            <Styled.Type>Subscription Expiration Date (NONPROD)</Styled.Type>
            <Styled.Value>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {ExpirationUtils.formatDate(subscriberRecord.nonProdSubscriptionExpirationDate)}
              {nonProdSubscriptionStatusIcon && (
                <Icon
                  iconName={nonProdSubscriptionStatusIcon.iconName}
                  aria-label={`Non-production subscription status: ${ExpirationUtils.getStatusText(subscriberRecord.nonProdSubscriptionExpirationStatus)}`}
                  styles={{ root: { fontSize: 18, color: nonProdSubscriptionStatusIcon.color } }}
                />
              )}
              </span>
            </Styled.Value>
          </Styled.TypeValueContainer>
          )}
        </Styled.PortalManagementDetails>
        <Styled.PortalManagementDetails>
          <Styled.TypeValueContainer>
            <Styled.Type>Service Tree ID</Styled.Type>
            <Styled.Value>
              <a href={`https://microsoftservicetree.com/services/${subscriberRecord.serviceTreeID}`} target="_blank" rel="noopener noreferrer" aria-label='Open Service Tree'>
                {subscriberRecord.serviceTreeID}&nbsp;
                <Icon
                  data-testid="Service-Tree-Link"
                  aria-label="Service Tree Link"
                  iconName="OpenInNewWindow"
                  aria-hidden="true"
                />
              </a>
            </Styled.Value>
          </Styled.TypeValueContainer>
          <Styled.TypeValueContainer>
            <Styled.Type>Service Tree Name</Styled.Type>
            <Styled.Value>{subscriberRecord.serviceTreeName}</Styled.Value>
          </Styled.TypeValueContainer>
          {subscriberRecord.productionAccountName && (
          <Styled.TypeValueContainer>
            <Styled.Type>DataLake Account Name (PROD)</Styled.Type>
            <Styled.Value>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {subscriberRecord.productionAccountName}
            <TooltipHost tooltipProps={getTooltipProps(subscriberRecord.productionAccountName)}>
              <IconButton
                    iconProps={{ iconName: 'Info' }}
                    aria-label={'DataLake Account Info'}
                    styles={{ root: { marginBottom: -3 } }}
              />
            </TooltipHost>
            </span>
            </Styled.Value>
          </Styled.TypeValueContainer>
          )}
          {subscriberRecord.nonProductionAccountName && (
          <Styled.TypeValueContainer>
            <Styled.Type>DataLake Account Name (NONPROD)</Styled.Type>
            <Styled.Value>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {subscriberRecord.nonProductionAccountName}
            <TooltipHost tooltipProps={getTooltipProps(subscriberRecord.nonProductionAccountName)}>
              <IconButton
                    iconProps={{ iconName: 'Info' }}
                    aria-label={'DataLake Account Info'}
                    styles={{ root: { marginBottom: -3 } }}
              />
            </TooltipHost>
            </span>
            </Styled.Value>
          </Styled.TypeValueContainer>
          )}
        </Styled.PortalManagementDetails>
      </Styled.DetailsContainer>
    </Styled.HeaderContainer>
  );
}

export function SubscriberDetailsHeaderError({message}:{message:string}):React.ReactElement{
  return(
     <Styled.ErrorContainer>
      <MessageBar data-testid="heading-section-error" messageBarType={MessageBarType.error}>
       {message}
      </MessageBar>
     </Styled.ErrorContainer>
  );
}